import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDomainMutation } from '@pps/mutations';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useEffect } from 'react';
import i18n from '@/i18n';
import { assertDeleted } from '@/lib/delete-guard';

export interface ClubNotification {
  id: string;
  club_id: string;
  user_id: string;
  type: string;
  title: string;
  body: string | null;
  metadata: Record<string, unknown>;
  is_read: boolean;
  created_at: string;
}

const NOTIFICATIONS_INVALIDATE = [['notifications'], ['all-notifications']] as const;

export function useNotifications() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['notifications', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('club_notifications')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_read', false)
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as ClubNotification[];
    },
    enabled: !!user,
  });

  // Realtime subscription
  useEffect(() => {
    if (!user) return;
    // UUID único por mount — evita 'cannot add callbacks after subscribe()'
    // quando Supabase-js v2 retorna channel cacheado (removeChannel async).
    const channel = supabase
      .channel(`notifications-realtime-${crypto.randomUUID()}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'club_notifications', filter: `user_id=eq.${user.id}` },
        () => {
          qc.invalidateQueries({ queryKey: ['notifications'] });
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user?.id, qc]);

  const markAsRead = useDomainMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('club_notifications').update({ is_read: true }).eq('id', id);
      if (error) throw error;
    },
    invalidates: NOTIFICATIONS_INVALIDATE,
    toast: { error: i18n.t('toasts.error') },
    tags: { feature: 'crm', action: 'mark_notification_read' },
  });

  const markAllAsRead = useDomainMutation({
    mutationFn: async () => {
      if (!user) throw new Error('missing user');
      const { error } = await supabase
        .from('club_notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);
      if (error) throw error;
    },
    invalidates: NOTIFICATIONS_INVALIDATE,
    toast: { error: i18n.t('toasts.error') },
    tags: { feature: 'crm', action: 'mark_all_notifications_read' },
  });

  return {
    notifications: query.data ?? [],
    isLoading: query.isLoading,
    markAsRead,
    markAllAsRead,
    unreadCount: query.data?.length ?? 0,
  };
}

/** All notifications (read + unread) for the Notifications page */
export function useAllNotifications() {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ['all-notifications', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('club_notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as ClubNotification[];
    },
    enabled: !!user,
  });

  const markAsRead = useDomainMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('club_notifications').update({ is_read: true }).eq('id', id);
      if (error) throw error;
    },
    invalidates: NOTIFICATIONS_INVALIDATE,
    toast: { error: i18n.t('toasts.error') },
    tags: { feature: 'crm', action: 'mark_notification_read' },
  });

  const markAllAsRead = useDomainMutation({
    mutationFn: async () => {
      if (!user) throw new Error('missing user');
      const { error } = await supabase
        .from('club_notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);
      if (error) throw error;
    },
    invalidates: NOTIFICATIONS_INVALIDATE,
    toast: { error: i18n.t('toasts.error') },
    tags: { feature: 'crm', action: 'mark_all_notifications_read' },
  });

  const deleteNotification = useDomainMutation({
    mutationFn: async (id: string) => {
      if (!user) throw new Error('missing user');
      const res = await supabase
        .from('club_notifications')
        .delete({ count: 'exact' })
        .eq('id', id)
        .eq('user_id', user.id);
      assertDeleted(res, 'notifications.delete');
    },
    invalidates: NOTIFICATIONS_INVALIDATE,
    toast: { success: i18n.t('toasts.notificationDeleted'), error: i18n.t('toasts.error') },
    tags: { feature: 'crm', action: 'delete_notification' },
  });

  const deleteAllRead = useDomainMutation({
    mutationFn: async () => {
      if (!user) throw new Error('missing user');
      // allowEmpty: "limpar as lidas" sem nenhuma lida e sucesso, nao falta de
      // permissao. O { count: 'exact' } fica para o erro continuar subindo.
      const res = await supabase
        .from('club_notifications')
        .delete({ count: 'exact' })
        .eq('user_id', user.id)
        .eq('is_read', true);
      assertDeleted(res, 'notifications.deleteAllRead', { allowEmpty: true });
    },
    invalidates: NOTIFICATIONS_INVALIDATE,
    toast: { success: i18n.t('toasts.notificationsCleared'), error: i18n.t('toasts.error') },
    tags: { feature: 'crm', action: 'delete_all_read_notifications' },
  });

  return {
    notifications: query.data ?? [],
    isLoading: query.isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllRead,
  };
}
